/**
 * TerrainTracks — Track-Register + Distanz-LOD der generischen 3D-Karte.
 *
 * Ein AR-Track (z. B. Tscheppaschlucht) ist die LOD-Einheit: weit draußen
 * ist sein gesamtes Detail (POIs, Force-Sim, Routen-Bänder) unsichtbar
 * klein — es wird gar nicht erst gerendert oder simuliert. Stattdessen:
 *
 *   Stufe 0 EMBLEM      (> silhouetteMeter): ein goldenes Siegel am Anker
 *   Stufe 1 SILHOUETTE  (detail..silhouette): + vereinfachte Routenlinie
 *   Stufe 2 DETAIL      (< detailMeter): voller Track — Pins, Sim, Bänder
 *
 * Distanzen in realen METERN (Kamera→Anker, worldUnitsPerMeter — dieselbe
 * Semantik wie Bridge-v0.4-`distanceMeters`). Die Schwellen sind DATEN des
 * region-gebackenen `tracks-<region>.json`, nie Frame-Konstanten.
 *
 * Detail hat immer genau EIN Track (v0.5-Review 3DPresenter): aus den
 * qualifizierten Tracks gewinnt der kameranächste, Tie-Break kleinste
 * trackId. Der Wechsel wird über `onAktiverDetailTrack(trackId|null)`
 * gemeldet — erst nach einem Stabilitätsfenster, damit programmatische
 * Kameraflüge (focusPoi, Übersichts-Zoom) keine Load/Unload-Kaskaden in
 * den Host feuern: der stabile Endzustand gewinnt. Zusätzlich ±5 %
 * Hysterese um beide Schwellen gegen Flattern im Orbit an der Grenze.
 * Ein nicht-aktiver, aber detail-naher Track bleibt auf Silhouette.
 *
 * Re-Drape ist event-getrieben (onHeightsChanged, gebündelt) — kein
 * Endlos-Poller.
 */
import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { erzeugeGoldMaterial, gebeGoldMaterialFrei } from './GoldMaterial';
import type { TerrainRoot } from './TerrainRoot';

export type TrackStufe = 0 | 1 | 2;

export interface TerrainTracksConfig {
    terrain: TerrainRoot;
    /** tracks-<region>.json ({ tracks: [{trackId, name, anker, stufen, silhouette}] }) */
    registerUrl?: string | null;
    getRenderer: () => THREE.WebGLRenderer;
    getKamera: () => THREE.Camera | null;
    /** Genau EIN Track darf Detail zeigen — hier kommt der Wechsel an
     *  (null = keiner qualifiziert). Entprellt: feuert erst, wenn der
     *  Kandidat ein Stabilitätsfenster lang derselbe war. */
    onAktiverDetailTrack?: (trackId: number | null) => void;
    /** Emblem-Größe in Metern (Kantenlänge des Siegels, Vorgabe 900) */
    emblemMeter?: number;
    /** Stabilitätsfenster in Sekunden vor einem Detail-Wechsel (Vorgabe 0.6) */
    stabilSekunden?: number;
    /** Commit-Gate: true während einer programmatischen Kamerafahrt —
     *  der Kandidat wird beobachtet, aber erst nach Fahrtende + Stabil-
     *  fenster committed (Flug-Garantie v0.5; Flüge dauern bis 2,4 s und
     *  überstehen jedes reine Zeitfenster). */
    istKameraInFahrt?: () => boolean;
    /** Sichtbarer Fehlerkanal fuer ungueltige Register-Eintraege. */
    onInvalidTrack?: (message: string) => void;
    /** Full-Replace abgeschlossen; 0 deaktiviert Track-LOD fuer die Instanz. */
    onRegisterChanged?: (validTrackCount: number) => void;
}

/** Renderer-unabhaengige v0.5-Form, wie sie der Host via MapBridge liefert. */
export interface TerrainTrackDefinition {
    trackId: number;
    name: string;
    lat: number;
    lng: number;
    detailMeter: number;
    silhouetteMeter: number;
    silhouette?: { lat: number; lng: number }[][];
}

interface TrackEintrag {
    trackId: number;
    name: string;
    anker: { lat: number; lng: number };
    stufen: { detailMeter: number; silhouetteMeter: number };
    silhouette?: [number, number][][];
}

interface Track {
    daten: TrackEintrag;
    x: number;
    z: number;
    anzeigeStufe: TrackStufe | -1;
    distMeter: number;
    emblem: THREE.Mesh;
    silhouette: THREE.Mesh | null;
}

/** ±5 % Hysterese: Aufstieg braucht klares Unterschreiten, Abstieg klares
 *  Überschreiten der Schwelle. */
const HYSTERESE = 0.05;

export class TerrainTracks extends SceneNode {
    private readonly _cfg: Required<TerrainTracksConfig>;
    private readonly _terrain: TerrainRoot;
    private _tracks: Track[] = [];
    private _gold: THREE.MeshStandardMaterial | null = null;
    private _siegelGeo: THREE.BufferGeometry | null = null;
    private _pruefUhr = 0;
    private _drapeAusstehend = false;
    private _drapeUhr = 0;
    private _aktiverDetailTrack: number | null = null;
    private _kandidat: number | null = null;
    private _kandidatSeit = 0;
    private _erstesUrteil = true;
    private _destroyed = false;
    private _unsubscribeHeights: (() => void) | null = null;
    private readonly _tmp = new THREE.Vector3();

    constructor(cfg: TerrainTracksConfig) {
        super('TerrainTracks');
        this._cfg = {
            registerUrl: null,
            onAktiverDetailTrack: () => undefined,
            emblemMeter: 900,
            stabilSekunden: 0.6,
            istKameraInFahrt: () => false,
            onInvalidTrack: message => console.error('[terrain]', message),
            onRegisterChanged: () => undefined,
            ...cfg,
        };
        this._terrain = cfg.terrain;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainTracks';
        this._mesh.userData.sceneNode = this;
        this._unsubscribeHeights = this._terrain.onHeightsChanged(() => { this._drapeAusstehend = true; });
        if (this._cfg.registerUrl) {
            this._lade(this._cfg.registerUrl).catch((e) => console.error('[terrain] Track-Register-Aufbau fehlgeschlagen:', e));
        }
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    private async _lade(registerUrl: string): Promise<void> {
        const doc = await fetch(registerUrl).then((r) => r.json()).catch(() => null);
        if (this._destroyed) return;
        const eintraege: TrackEintrag[] = Array.isArray(doc?.tracks) ? doc.tracks : [];
        if (eintraege.length === 0) {
            console.warn('[terrain] Track-Register leer/nicht ladbar:', registerUrl);
            this._ersetzeTracks([]);
            return;
        }
        this._ersetzeTracks(eintraege);
    }

    /** v0.5 Full-Replace. Rueckgabe = Zahl der gueltigen Tracks. */
    setTracks(definitions: TerrainTrackDefinition[]): number {
        const eintraege: TrackEintrag[] = definitions.map(track => ({
            trackId: track.trackId,
            name: track.name,
            anker: { lat: track.lat, lng: track.lng },
            stufen: { detailMeter: track.detailMeter, silhouetteMeter: track.silhouetteMeter },
            silhouette: track.silhouette?.map(line => line.map(point => [point.lng, point.lat])),
        }));
        return this._ersetzeTracks(eintraege);
    }

    private _ersetzeTracks(eintraege: TrackEintrag[]): number {
        const vorherAktiv = this._aktiverDetailTrack;
        this._raeumeVisualsAuf();
        this._aktiverDetailTrack = null;
        this._kandidat = null;
        this._kandidatSeit = 0;
        this._erstesUrteil = true;
        if (vorherAktiv !== null) this._cfg.onAktiverDetailTrack(null);

        const gesehen = new Set<number>();
        const gueltig = eintraege.filter((t) => this._istGueltig(t, gesehen));
        if (gueltig.length > 0) {
            this._baue(gueltig);
            this._drapeAusstehend = true;
        }
        this._cfg.onRegisterChanged(gueltig.length);
        return gueltig.length;
    }

    /** Register-Validierung (v0.5): ungültige Einträge sichtbar melden,
     *  übrige Tracks bleiben funktionsfähig. */
    private _istGueltig(t: TrackEintrag, gesehen: Set<number>): boolean {
        const ok = Number.isInteger(t?.trackId) && t.trackId > 0
            && Number.isFinite(t?.anker?.lat) && Number.isFinite(t?.anker?.lng)
            && Number.isFinite(t?.stufen?.detailMeter) && Number.isFinite(t?.stufen?.silhouetteMeter)
            && t.stufen.detailMeter > 0 && t.stufen.detailMeter < t.stufen.silhouetteMeter
            && !gesehen.has(t.trackId);
        if (!ok) {
            const message = `Track-Register: ungueltiger Eintrag uebersprungen: ${JSON.stringify(t)?.slice(0, 200)}`;
            this._cfg.onInvalidTrack(message);
        }
        else gesehen.add(t.trackId);
        return ok;
    }

    private _baue(eintraege: TrackEintrag[]): void {
        const proj = this._terrain.projection;
        this._gold = erzeugeGoldMaterial(this._cfg.getRenderer(), 0.22);
        const kante = this._cfg.emblemMeter * proj.unitsPerMeter;
        // Siegel: liegender Gold-Oktaeder — liest sich aus jeder Richtung,
        // ist mit 8 Dreiecken praktisch gratis; Geometrie wird geteilt
        this._siegelGeo = new THREE.OctahedronGeometry(kante / 2);
        this._siegelGeo.scale(1, 0.55, 1);

        for (const t of eintraege) {
            const w = proj.geoToWorld(t.anker.lng, t.anker.lat, 0);
            const emblem = new THREE.Mesh(this._siegelGeo, this._gold);
            emblem.name = `track/${t.trackId}/emblem`;
            emblem.position.set(w.x, 0.3, w.z);
            emblem.visible = false;
            (this._mesh as THREE.Group).add(emblem);

            let silhouette: THREE.Mesh | null = null;
            if (t.silhouette?.length) {
                silhouette = this._baueSilhouette(t.silhouette, this._gold);
                silhouette.name = `track/${t.trackId}/silhouette`;
                silhouette.visible = false;
                (this._mesh as THREE.Group).add(silhouette);
            }
            this._tracks.push({ daten: t, x: w.x, z: w.z, anzeigeStufe: -1, distMeter: Infinity, emblem, silhouette });
        }
    }

    /** Vereinfachte Routenlinien als schmale, flache Gold-Bänder. */
    private _baueSilhouette(linien: [number, number][][], material: THREE.Material): THREE.Mesh {
        const proj = this._terrain.projection;
        const halb = (35 * proj.unitsPerMeter) / 2; // 35 m Bandbreite — Übersichts-Maßstab
        const pos: number[] = [];
        const idx: number[] = [];
        for (const linie of linien) {
            if (linie.length < 2) continue;
            const pts = linie.map(([lng, lat]) => proj.geoToWorld(lng, lat, 0));
            const basis = pos.length / 3;
            for (let i = 0; i < pts.length; i++) {
                const vor = pts[Math.max(0, i - 1)];
                const nach = pts[Math.min(pts.length - 1, i + 1)];
                let dx = nach.x - vor.x, dz = nach.z - vor.z;
                const len = Math.hypot(dx, dz) || 1;
                dx /= len; dz /= len;
                pos.push(pts[i].x - dz * halb, 0, pts[i].z + dx * halb);
                pos.push(pts[i].x + dz * halb, 0, pts[i].z - dx * halb);
            }
            for (let i = 0; i < pts.length - 1; i++) {
                const a = basis + i * 2;
                idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
            }
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
        geo.setIndex(idx);
        const mesh = new THREE.Mesh(geo, material);
        mesh.frustumCulled = false;
        return mesh;
    }

    /** Emblem + Silhouette dem Gelände nachführen (event-getrieben). */
    private _drape(): void {
        const proj = this._terrain.projection;
        for (const t of this._tracks) {
            const y = this._terrain.getWorldY(t.x, t.z);
            if (y !== null) t.emblem.position.y = y + 150 * proj.unitsPerMeter;
            if (t.silhouette) {
                const arr = t.silhouette.geometry.getAttribute('position') as THREE.BufferAttribute;
                const a = arr.array as Float32Array;
                for (let v = 0; v < arr.count; v++) {
                    const gy = this._terrain.getWorldY(a[v * 3], a[v * 3 + 2]);
                    if (gy !== null) a[v * 3 + 1] = gy + 12 * proj.unitsPerMeter;
                }
                arr.needsUpdate = true;
                t.silhouette.geometry.computeBoundingSphere();
            }
        }
    }

    protected onUpdate(deltaTime: number): void {
        if (this._destroyed || this._tracks.length === 0) return;

        // Re-Drape gebündelt nach Height-Revisionen (max 2×/s)
        this._drapeUhr += deltaTime;
        if (this._drapeAusstehend && this._drapeUhr >= 0.5) {
            this._drapeAusstehend = false;
            this._drapeUhr = 0;
            this._drape();
        }

        // Stufen-Prüfung: 4×/s reicht — Kamerafahrten sind langsamer
        this._pruefUhr += deltaTime;
        if (this._pruefUhr < 0.25) return;
        const dt = this._pruefUhr;
        this._pruefUhr = 0;
        const kamera = this._cfg.getKamera();
        if (!kamera) return;
        const kamPos = kamera.getWorldPosition(this._tmp);
        const proj = this._terrain.projection;

        // 1) Distanzen + Detail-Kandidat: kameranächster qualifizierter
        //    Track, Tie-Break kleinste trackId (stabil bei exakt gleicher
        //    Distanz); Hysterese hält den amtierenden Track länger drin.
        let kandidat: Track | null = null;
        for (const t of this._tracks) {
            t.distMeter = Math.hypot(kamPos.x - t.x, kamPos.y - t.emblem.position.y, kamPos.z - t.z) / proj.unitsPerMeter;
            const amtierend = this._aktiverDetailTrack === t.daten.trackId;
            const schwelle = t.daten.stufen.detailMeter * (amtierend ? 1 + HYSTERESE : 1 - HYSTERESE);
            if (t.distMeter >= schwelle) continue;
            if (!kandidat || t.distMeter < kandidat.distMeter
                || (t.distMeter === kandidat.distMeter && t.daten.trackId < kandidat.daten.trackId)) {
                kandidat = t;
            }
        }

        // 2) Entprellung: der Host-Wechsel feuert erst, wenn derselbe
        //    Kandidat ein Stabilitätsfenster lang ansteht — transiente
        //    Zwischenstufen von Kameraflügen erreichen den Host nie.
        const kandidatId = kandidat?.daten.trackId ?? null;
        if (kandidatId !== this._kandidat) {
            this._kandidat = kandidatId;
            this._kandidatSeit = 0;
        } else {
            this._kandidatSeit += dt;
        }
        /* Der allererste Zyklus committet ohne Stabilfenster: der Start-
         * zustand ist kein Kameraflug, und die Detail-Nodes beginnen
         * schlafend — bei nahem Start sollen sie sofort erwachen. */
        /* Commit-Gate: waehrend einer programmatischen Fahrt wird nur
         * beobachtet — der stabile Endzustand NACH der Fahrt gewinnt. */
        if (!this._cfg.istKameraInFahrt()
            && ((this._kandidat !== this._aktiverDetailTrack && this._kandidatSeit >= this._cfg.stabilSekunden)
                || this._erstesUrteil)) {
            this._erstesUrteil = false;
            if (this._kandidat !== this._aktiverDetailTrack) {
                this._aktiverDetailTrack = this._kandidat;
                this._cfg.onAktiverDetailTrack(this._aktiverDetailTrack);
            }
        }

        // 3) Anzeige pro Track: nur der AKTIVE Detail-Track blendet sein
        //    Emblem aus; ein qualifizierter, aber nicht aktiver Track bleibt
        //    auf Silhouette.
        for (const t of this._tracks) {
            const istAktiv = this._aktiverDetailTrack === t.daten.trackId;
            const aufSil = t.anzeigeStufe >= 1;
            const silSchwelle = t.daten.stufen.silhouetteMeter * (aufSil ? 1 + HYSTERESE : 1 - HYSTERESE);
            const stufe: TrackStufe = istAktiv ? 2 : t.distMeter < silSchwelle ? 1 : 0;
            if (stufe === t.anzeigeStufe) continue;
            t.anzeigeStufe = stufe;
            t.emblem.visible = stufe < 2;
            if (t.silhouette) t.silhouette.visible = stufe === 1;
        }
    }

    protected override onDestroy(): void {
        this._destroyed = true;
        this._unsubscribeHeights?.();
        this._unsubscribeHeights = null;
        this._raeumeVisualsAuf();
    }

    private _raeumeVisualsAuf(): void {
        for (const t of this._tracks) {
            (this._mesh as THREE.Group | null)?.remove(t.emblem);
            if (t.silhouette) (this._mesh as THREE.Group | null)?.remove(t.silhouette);
            t.silhouette?.geometry.dispose();
        }
        this._siegelGeo?.dispose();
        this._siegelGeo = null;
        if (this._gold) {
            gebeGoldMaterialFrei(this._gold);
            this._gold = null;
        }
        this._tracks = [];
    }

    refreshSurface(): void {
        this._drapeAusstehend = true;
    }
}
